import React, { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { FlaskConical, LogOut, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { isSandboxActive, setSandboxActive, onSandboxChange } from '../../lib/sandboxMode';
import api from '../../services/api';

// Bandeau orange permanent affiché tant que le mode Bac à sable (formation) est actif.
// Décale le contenu de la page (padding-top sur <html>) pour ne rien masquer.
// Affiche un compteur d'éléments créés en formation + un bouton de nettoyage sélectif.
const SandboxBanner = () => {
  const { t } = useTranslation('admin');
  const [active, setActive] = useState(isSandboxActive());
  const [count, setCount] = useState(null);
  const [cleaning, setCleaning] = useState(false);

  useEffect(() => onSandboxChange(setActive), []);

  useEffect(() => {
    const el = document.documentElement;
    el.style.paddingTop = active ? '40px' : '';
    return () => { el.style.paddingTop = ''; };
  }, [active]);

  const loadStats = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/sandbox/stats');
      setCount(typeof data?.total === 'number' ? data.total : null);
    } catch {
      setCount(null);
    }
  }, []);

  useEffect(() => {
    if (active) loadStats();
  }, [active, loadStats]);

  if (!active) return null;

  const exit = () => {
    setSandboxActive(false);
    setTimeout(() => window.location.reload(), 100);
  };

  const cleanup = async () => {
    if (!window.confirm(t('sandbox.confirm'))) return;
    setCleaning(true);
    try {
      const { data } = await api.post('/admin/sandbox/cleanup');
      toast.success(t('sandbox.cleanDone', { count: data?.deleted_total || 0 }));
      await loadStats();
      setTimeout(() => window.location.reload(), 600);
    } catch {
      toast.error(t('sandbox.cleanError'));
    } finally {
      setCleaning(false);
    }
  };

  return createPortal((
    <div
      data-testid="sandbox-banner"
      className="fixed top-0 left-0 right-0 z-[9999] h-10 flex items-center justify-center gap-3 px-4 bg-orange-500 text-white shadow-md select-none"
      style={{ animation: 'sandboxPulse 2.5s ease-in-out infinite' }}
    >
      <FlaskConical size={16} className="shrink-0" />
      <span className="text-sm font-bold tracking-wide uppercase">
        {t('sandbox.bannerTitle')}
      </span>

      {count !== null && (
        <span
          data-testid="sandbox-formation-count"
          className="hidden sm:inline-flex items-center rounded-full bg-white/20 px-2.5 py-0.5 text-xs font-semibold"
        >
          {t('sandbox.count', { count })}
        </span>
      )}

      {count > 0 && (
        <button
          data-testid="sandbox-cleanup-btn"
          onClick={cleanup}
          disabled={cleaning}
          className="inline-flex items-center gap-1.5 rounded-full bg-white text-orange-600 hover:bg-orange-50 disabled:opacity-60 transition-colors px-3 py-1 text-xs font-bold"
        >
          {cleaning ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
          {t('sandbox.clean')}
        </button>
      )}

      <button
        data-testid="sandbox-banner-exit"
        onClick={exit}
        className="ml-1 inline-flex items-center gap-1.5 rounded-full bg-white/20 hover:bg-white/30 transition-colors px-3 py-1 text-xs font-semibold"
      >
        <LogOut size={13} /> {t('sandbox.quit')}
      </button>
      <style>{`@keyframes sandboxPulse { 0%,100%{opacity:1} 50%{opacity:.88} }`}</style>
    </div>
  ), document.body);
};

export default SandboxBanner;
